// FAQ.jsx
// Public FAQ page for Diagnostic Intelligence.
//
// - Content lives in ./faqData.js (edit copy there, not here).
// - Search across questions, answers and IDs (all words must match).
// - Section filter with live counts, expand/collapse all.
// - Deep links: /faq#SCI-05 opens and scrolls to that question.
// - No dependencies beyond React. Styles are scoped under .di-faq.
//
// Theme hooks (optional, set on a parent or :root):
//   --di-font-serif, --di-font-sans   override the font stacks
//   --di-faq-sticky-top               offset under your sticky navbar (default 96px)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  faqMeta,
  evidencePrinciple,
  readingNote,
  faqParts,
  faqClosing,
  faqTotal,
} from "../data/faqData";

/* ------------------------------------------------------------------ helpers */

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const parseTerms = (query) => query.toLowerCase().split(/\s+/).filter(Boolean);

const matchesAll = (haystack, terms) => {
  const h = haystack.toLowerCase();
  return terms.every((t) => h.includes(t));
};

function Highlight({ text, terms }) {
  if (terms.length === 0) return <>{text}</>;
  const re = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi");
  return (
    <>
      {text.split(re).map((chunk, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="di-faq__mark">
            {chunk}
          </mark>
        ) : (
          <React.Fragment key={i}>{chunk}</React.Fragment>
        )
      )}
    </>
  );
}

const allIds = new Set(faqParts.flatMap((p) => p.items.map((i) => i.id)));

/* ---------------------------------------------------------------- component */

export default function FAQ() {
  const [query, setQuery] = useState("");
  const [lens, setLens] = useState("all");
  const [open, setOpen] = useState(() => new Set());
  const [copied, setCopied] = useState({ id: "", mode: "" });
  const pendingScroll = useRef(null);
  const copyTimer = useRef(null);

  const terms = useMemo(() => parseTerms(query), [query]);

  // Every part with its items filtered by the search terms.
  const searched = useMemo(
    () =>
      faqParts.map((part) => ({
        ...part,
        items: part.items.filter((item) =>
          matchesAll(`${item.id} ${item.q} ${item.a}`, terms)
        ),
      })),
    [terms]
  );

  const matchTotal = searched.reduce((n, p) => n + p.items.length, 0);
  const shownParts = searched.filter(
    (p) => (lens === "all" || p.id === lens) && p.items.length > 0
  );
  const shownItems = shownParts.flatMap((p) => p.items);
  const shownCount = shownItems.length;
  const allShownOpen = shownCount > 0 && shownItems.every((i) => open.has(i.id));

  /* ---- deep links ---- */
  const openFromHash = useCallback(() => {
    const raw = decodeURIComponent(window.location.hash.replace(/^#/, ""))
      .trim()
      .toUpperCase();
    if (!raw || !allIds.has(raw)) return;
    setQuery("");
    setLens("all");
    setOpen((prev) => new Set(prev).add(raw));
    pendingScroll.current = raw;
  }, []);

  useEffect(() => {
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openFromHash]);

  useEffect(() => {
    if (!pendingScroll.current) return;
    const el = document.getElementById(`faq-${pendingScroll.current}`);
    if (el) {
      el.scrollIntoView({ block: "start" });
      pendingScroll.current = null;
    }
  }, [open, lens, query]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  /* ---- actions ---- */
  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandShown = () =>
    setOpen((prev) => {
      const next = new Set(prev);
      shownItems.forEach((i) => next.add(i.id));
      return next;
    });

  const collapseShown = () =>
    setOpen((prev) => {
      const next = new Set(prev);
      shownItems.forEach((i) => next.delete(i.id));
      return next;
    });

  const copyLink = async (id) => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${id}`;
    let mode = "copied";
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      // Clipboard blocked: put the link in the address bar so it can be copied there.
      window.history.replaceState(null, "", `#${id}`);
      mode = "address";
    }
    setCopied({ id, mode });
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied({ id: "", mode: "" }), 2500);
  };

  const resetSearch = () => {
    setQuery("");
    setLens("all");
  };

  /* ------------------------------------------------------------------ render */
  return (
    <section className="di-faq" aria-labelledby="di-faq-title">
      <style>{css}</style>

      {/* ---------- header ---------- */}
      <header className="di-faq__hero">
        <h1 id="di-faq-title" className="di-faq__title">
          {faqMeta.titleLead}{" "}
          <span className="di-faq__accent">{faqMeta.titleAccent}</span>
        </h1>
        <p className="di-faq__edition">
          {faqMeta.edition}, {faqMeta.date}. {faqTotal} questions in{" "}
          {faqParts.length} sections.
        </p>
        <div className="di-faq__about">
          {faqMeta.about.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      </header>

      {/* ---------- evidence principle ---------- */}
      <section className="di-faq__principle" aria-labelledby="di-faq-principle">
        <h2 id="di-faq-principle" className="di-faq__h2">
          {evidencePrinciple.title}
        </h2>
        <dl className="di-faq__principle-grid">
          {evidencePrinciple.rows.map((row) => (
            <div key={row.term} className="di-faq__principle-row">
              <dt>{row.term}</dt>
              <dd>{row.text}</dd>
            </div>
          ))}
        </dl>
        <p className="di-faq__reading">
          <strong>{readingNote.title}.</strong> {readingNote.text}
        </p>
      </section>

      {/* ---------- body ---------- */}
      <div className="di-faq__layout">
        <aside className="di-faq__side" aria-label="Search and sections">
          <div className="di-faq__search">
            <label htmlFor="di-faq-search" className="di-faq__sr">
              Search the FAQ
            </label>
            <input
              id="di-faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions and answers"
              autoComplete="off"
              spellCheck="false"
            />
            {query && (
              <button
                type="button"
                className="di-faq__clear"
                onClick={() => setQuery("")}
              >
                Clear
              </button>
            )}
          </div>

          <nav aria-label="FAQ sections">
            <ul className="di-faq__lenses">
              <li>
                <button
                  type="button"
                  className="di-faq__lens"
                  aria-pressed={lens === "all"}
                  onClick={() => setLens("all")}
                >
                  <span>All questions</span>
                  <span className="di-faq__count">{matchTotal}</span>
                </button>
              </li>
              {searched.map((part) => (
                <li key={part.id}>
                  <button
                    type="button"
                    className="di-faq__lens"
                    aria-pressed={lens === part.id}
                    onClick={() => setLens(part.id)}
                  >
                    <span>{part.short}</span>
                    <span className="di-faq__count">{part.items.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="di-faq__main">
          <div className="di-faq__toolbar">
            <p className="di-faq__status" role="status" aria-live="polite">
              {terms.length > 0
                ? `${shownCount} of ${faqTotal} questions match`
                : `Showing ${shownCount} of ${faqTotal} questions`}
            </p>
            {shownCount > 0 && (
              <button
                type="button"
                className="di-faq__btn"
                onClick={allShownOpen ? collapseShown : expandShown}
              >
                {allShownOpen ? "Collapse all" : "Expand all"}
              </button>
            )}
          </div>

          {shownCount === 0 && (
            <div className="di-faq__empty">
              <p>
                No questions match{query ? ` “${query}”` : " this filter"}. Try
                fewer or shorter words, or search by a question ID such as
                SCI-05.
              </p>
              <button type="button" className="di-faq__btn" onClick={resetSearch}>
                Show all questions
              </button>
            </div>
          )}

          {shownParts.map((part) => (
            <section
              key={part.id}
              className="di-faq__part"
              aria-labelledby={`faq-part-${part.id}`}
            >
              <h2 id={`faq-part-${part.id}`} className="di-faq__h2">
                {part.title}
              </h2>
              <p className="di-faq__part-desc">{part.description}</p>

              <ul className="di-faq__list">
                {part.items.map((item) => {
                  const isOpen = open.has(item.id);
                  const panelId = `faq-panel-${item.id}`;
                  const inAnswerOnly =
                    terms.length > 0 &&
                    !matchesAll(`${item.id} ${item.q}`, terms);
                  const copyState = copied.id === item.id ? copied.mode : "";

                  return (
                    <li
                      key={item.id}
                      id={`faq-${item.id}`}
                      className={`di-faq__item${isOpen ? " is-open" : ""}`}
                    >
                      <h3 className="di-faq__q">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => toggle(item.id)}
                        >
                          <span className="di-faq__id">
                            <Highlight text={item.id} terms={terms} />
                          </span>
                          <span className="di-faq__qtext">
                            <Highlight text={item.q} terms={terms} />
                            {!isOpen && inAnswerOnly && (
                              <span className="di-faq__hint">
                                Matches text in the answer
                              </span>
                            )}
                          </span>
                          <span className="di-faq__chev" aria-hidden="true" />
                        </button>
                      </h3>

                      <div id={panelId} className="di-faq__a" hidden={!isOpen}>
                        <p>
                          <Highlight text={item.a} terms={terms} />
                        </p>
                        <div className="di-faq__a-foot">
                          <button
                            type="button"
                            className="di-faq__link"
                            onClick={() => copyLink(item.id)}
                          >
                            Copy link to this question
                          </button>
                          <span className="di-faq__copied" role="status">
                            {copyState === "copied" && "Link copied"}
                            {copyState === "address" &&
                              "Link is in the address bar"}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {/* ---------- closing ---------- */}
          {lens === "all" && terms.length === 0 && (
            <section className="di-faq__closing" aria-labelledby="di-faq-closing">
              <h2 id="di-faq-closing" className="di-faq__h2">
                {faqClosing.title}
              </h2>
              {faqClosing.paragraphs.map((p) => (
                <p key={p}>{p}</p>
              ))}
              <dl className="di-faq__closing-rows">
                {faqClosing.rows.map((row) => (
                  <div key={row.term}>
                    <dt>{row.term}</dt>
                    <dd>{row.text}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- styles */

const css = `
.di-faq {
  --faq-teal-900: #085041;
  --faq-teal-700: #0F6E56;
  --faq-teal-300: #5DCAA5;
  --faq-ink: #12302a;
  --faq-muted: #4d645c;
  --faq-line: #d3e5de;
  --faq-wash: #f3f9f6;
  --faq-shadow: 6px 6px 0 rgba(15, 110, 86, 0.14);
  --faq-serif: var(--di-font-serif, Georgia, "Times New Roman", serif);
  --faq-sans: var(--di-font-sans, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif);

  max-width: 1120px;
  margin: 0 auto;
  padding: 48px 20px 88px;
  font-family: var(--faq-sans);
  color: var(--faq-ink);
  line-height: 1.6;
}
.di-faq *, .di-faq *::before, .di-faq *::after { box-sizing: border-box; }
.di-faq button, .di-faq input { font: inherit; color: inherit; }
.di-faq p { margin: 0; }
.di-faq ul { list-style: none; margin: 0; padding: 0; }
.di-faq dl, .di-faq dd { margin: 0; }
.di-faq h1, .di-faq h2, .di-faq h3 { margin: 0; }

.di-faq__sr {
  position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

.di-faq button:focus-visible,
.di-faq input:focus-visible {
  outline: 2px solid var(--faq-teal-700);
  outline-offset: 2px;
}

/* ---------- hero ---------- */
.di-faq__hero { max-width: 760px; }
.di-faq__title {
  font-family: var(--faq-serif);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.1rem);
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--faq-ink);
}
.di-faq__accent { color: var(--faq-teal-700); }
.di-faq__edition {
  margin-top: 14px;
  font-size: 0.95rem;
  color: var(--faq-muted);
}
.di-faq__about { margin-top: 20px; display: grid; gap: 12px; }
.di-faq__about p { font-size: 1.05rem; max-width: 68ch; }

/* ---------- evidence principle ---------- */
.di-faq__principle {
  margin-top: 40px;
  padding: 26px 28px 24px;
  background: #fff;
  border: 1px solid var(--faq-line);
  border-radius: 10px;
  box-shadow: var(--faq-shadow);
}
.di-faq__h2 {
  font-family: var(--faq-serif);
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1.25;
  color: var(--faq-teal-900);
}
.di-faq__principle-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px 32px;
}
.di-faq__principle-row dt {
  font-weight: 700;
  color: var(--faq-teal-700);
  margin-bottom: 2px;
}
.di-faq__principle-row dd { font-size: 0.97rem; max-width: 52ch; }
.di-faq__reading {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--faq-line);
  font-size: 0.93rem;
  color: var(--faq-muted);
  max-width: 78ch;
}
.di-faq__reading strong { color: var(--faq-ink); }

/* ---------- layout ---------- */
.di-faq__layout {
  margin-top: 48px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: start;
}
.di-faq__side { min-width: 0; }

/* search */
.di-faq__search { position: relative; }
.di-faq__search input {
  width: 100%;
  padding: 12px 70px 12px 14px;
  background: #fff;
  border: 1px solid var(--faq-teal-700);
  border-radius: 8px;
  font-size: 0.97rem;
}
.di-faq__search input::placeholder { color: var(--faq-muted); }
.di-faq__clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  padding: 6px 10px;
  background: transparent; border: 0; border-radius: 6px;
  font-size: 0.85rem; font-weight: 600; color: var(--faq-teal-700);
  cursor: pointer;
}
.di-faq__clear:hover { background: var(--faq-wash); }

/* section filter */
.di-faq__lenses {
  margin-top: 14px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
}
.di-faq__lens {
  display: flex; align-items: center; gap: 10px; justify-content: space-between;
  white-space: nowrap;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid var(--faq-line);
  border-radius: 8px;
  font-size: 0.93rem;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;
}
.di-faq__lens:hover { border-color: var(--faq-teal-700); }
.di-faq__lens[aria-pressed="true"] {
  background: var(--faq-teal-900);
  border-color: var(--faq-teal-900);
  color: #fff;
}
.di-faq__count {
  font-variant-numeric: tabular-nums;
  font-size: 0.82rem;
  color: var(--faq-muted);
}
.di-faq__lens[aria-pressed="true"] .di-faq__count { color: #bfeadb; }

/* toolbar */
.di-faq__toolbar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-bottom: 8px;
}
.di-faq__status { font-size: 0.9rem; color: var(--faq-muted); }
.di-faq__btn {
  padding: 8px 14px;
  background: #fff;
  border: 1px solid var(--faq-teal-700);
  border-radius: 8px;
  font-size: 0.9rem; font-weight: 600; color: var(--faq-teal-700);
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}
.di-faq__btn:hover { background: var(--faq-teal-700); color: #fff; }

.di-faq__empty {
  margin-top: 16px;
  padding: 24px;
  background: var(--faq-wash);
  border: 1px solid var(--faq-line);
  border-radius: 10px;
  display: grid; gap: 14px; justify-items: start;
}

/* ---------- parts and items ---------- */
.di-faq__part { margin-top: 36px; }
.di-faq__part:first-of-type { margin-top: 20px; }
.di-faq__part-desc { margin-top: 6px; color: var(--faq-muted); max-width: 68ch; }
.di-faq__list { margin-top: 18px; display: flex; flex-direction: column; gap: 10px; }

.di-faq__item {
  scroll-margin-top: var(--di-faq-sticky-top, 96px);
  background: #fff;
  border: 1px solid var(--faq-line);
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.di-faq__item:hover { border-color: var(--faq-teal-300); }
.di-faq__item.is-open {
  border-color: var(--faq-teal-700);
  box-shadow: var(--faq-shadow);
}

.di-faq__q button {
  width: 100%;
  display: grid;
  grid-template-columns: 4.6rem 1fr 20px;
  grid-template-areas: "id q chev";
  column-gap: 12px;
  align-items: start;
  padding: 16px 18px;
  background: transparent;
  border: 0;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
}
.di-faq__id {
  grid-area: id;
  padding-top: 0.2em;
  font-size: 0.8rem; font-weight: 700; letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--faq-teal-700);
}
.di-faq__qtext {
  grid-area: q;
  font-weight: 600; font-size: 1.02rem; line-height: 1.4;
  display: flex; flex-direction: column; gap: 4px;
}
.di-faq__hint { font-size: 0.82rem; font-weight: 400; color: var(--faq-muted); }
.di-faq__chev {
  grid-area: chev;
  justify-self: end;
  margin-top: 0.4em;
  width: 9px; height: 9px;
  border-right: 2px solid var(--faq-teal-700);
  border-bottom: 2px solid var(--faq-teal-700);
  transform: rotate(45deg);
  transition: transform 0.15s;
}
.di-faq__item.is-open .di-faq__chev { transform: rotate(225deg); margin-top: 0.7em; }

.di-faq__a { padding: 0 18px 18px calc(18px + 4.6rem + 12px); }
.di-faq__a[hidden] { display: none; }
.di-faq__a p { max-width: 66ch; font-size: 1rem; line-height: 1.65; }
.di-faq__a-foot {
  margin-top: 14px;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.di-faq__link {
  padding: 0; background: none; border: 0;
  font-size: 0.87rem; font-weight: 600; color: var(--faq-teal-700);
  text-decoration: underline; text-underline-offset: 3px;
  cursor: pointer;
}
.di-faq__copied { font-size: 0.85rem; color: var(--faq-muted); }

.di-faq__mark {
  background: rgba(93, 202, 165, 0.38);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}

/* ---------- closing ---------- */
.di-faq__closing {
  margin-top: 56px;
  padding: 26px 28px;
  background: var(--faq-wash);
  border: 1px solid var(--faq-line);
  border-radius: 10px;
  display: grid; gap: 12px;
}
.di-faq__closing p { max-width: 68ch; }
.di-faq__closing-rows { margin-top: 8px; display: grid; gap: 14px; }
.di-faq__closing-rows dt { font-weight: 700; color: var(--faq-teal-700); }
.di-faq__closing-rows dd { max-width: 60ch; }

/* ---------- wide screens ---------- */
@media (min-width: 720px) {
  .di-faq__principle-grid { grid-template-columns: 1fr 1fr; }
}
@media (min-width: 940px) {
  .di-faq__layout { grid-template-columns: 240px minmax(0, 1fr); gap: 44px; }
  .di-faq__side {
    position: sticky;
    top: var(--di-faq-sticky-top, 96px);
  }
  .di-faq__lenses { flex-direction: column; overflow: visible; padding-bottom: 0; }
}

/* ---------- small screens ---------- */
@media (max-width: 600px) {
  .di-faq { padding: 32px 16px 64px; }
  .di-faq__principle, .di-faq__closing { padding: 20px 18px; }
  .di-faq__q button {
    grid-template-columns: 1fr 20px;
    grid-template-areas: "id chev" "q q";
    row-gap: 4px;
  }
  .di-faq__a { padding: 0 18px 18px; }
}

@media (prefers-reduced-motion: reduce) {
  .di-faq *, .di-faq *::before, .di-faq *::after { transition: none !important; }
}
`;
